import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, ArrowLeft, Link2, PenLine, Download, ExternalLink,
  Newspaper, Search, Globe, Loader2, Folder, Tag, X, Wand2,
} from "lucide-react";
import { useList } from "../../pages/admin/AdminApp";
import { useCrud } from "../../lib/adminApi";
import { api, img, errMsg } from "../../lib/api";
import { tr } from "../../lib/i18n";
import { Field, I18nInput, ImageUpload } from "./Fields";
import RichEditor from "./RichEditor";
import SortableList from "./SortableList";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { Checkbox } from "../ui/checkbox";

const UNCATEGORIZED = "— Kategorisiz —";

/* Inline category picker: dropdown + "Yeni kategori ekle" that persists in settings. */
function CategoryPicker({ value, options, onChange, testid, allowBlank = true, blankLabel = "— Kategorisiz —" }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [newCat, setNewCat] = useState("");
  const [busy, setBusy] = useState(false);
  const list = Array.isArray(options) ? options : [];
  const extra = value && !list.includes(value) ? [value] : [];

  const saveNew = async () => {
    const v = newCat.trim();
    if (!v) return;
    if (list.some((c) => c.toLowerCase() === v.toLowerCase())) {
      onChange(list.find((c) => c.toLowerCase() === v.toLowerCase()));
      setAdding(false); setNewCat("");
      return;
    }
    setBusy(true);
    try {
      const { data: site } = await api.get("/public/site");
      const press = site?.press || {};
      const next = [...(press.categories || []), v].slice(0, 40);
      await api.put("/admin/settings/press", { ...press, categories: next });
      onChange(v);
      qc.invalidateQueries({ queryKey: ["site"] });
      toast.success(`“${v}” kategorisi eklendi`);
      setAdding(false); setNewCat("");
    } catch (e) {
      toast.error(errMsg(e) || "Kategori eklenemedi");
    }
    setBusy(false);
  };

  if (adding) {
    return (
      <div className="flex gap-1.5" data-testid={`${testid}-adding`}>
        <Input
          value={newCat}
          onChange={(e) => setNewCat(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveNew(); } if (e.key === "Escape") { setAdding(false); setNewCat(""); } }}
          placeholder="Yeni kategori adı"
          autoFocus
          className="h-9 text-sm"
          data-testid={`${testid}-new-input`}
        />
        <Button type="button" size="sm" onClick={saveNew} disabled={busy || !newCat.trim()} data-testid={`${testid}-new-save`}>
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => { setAdding(false); setNewCat(""); }} data-testid={`${testid}-new-cancel`}>
          <X size={13} />
        </Button>
      </div>
    );
  }
  return (
    <div className="flex gap-1.5">
      <select
        className="w-full border rounded-md h-9 px-3 text-sm bg-white"
        value={value || ""}
        onChange={(e) => {
          if (e.target.value === "__new__") { setAdding(true); return; }
          onChange(e.target.value);
        }}
        data-testid={testid}
      >
        {allowBlank && <option value="">{blankLabel}</option>}
        {list.map((c) => <option key={c} value={c}>{c}</option>)}
        {extra.map((c) => <option key={c} value={c}>{c}</option>)}
        <option value="__new__">+ Yeni kategori ekle…</option>
      </select>
    </div>
  );
}

/* ------------------- SEARCH BLOCK ------------------- */

export function PressSearchPanel({ compact = false }) {
  const qc = useQueryClient();
  const { data: site } = useQuery({ queryKey: ["site"], queryFn: () => api.get("/public/site").then((r) => r.data) });
  const sources = site?.press?.sources || [];
  const categories = site?.press?.categories || [];
  const defaultQ = site?.press?.search_default_query || "Olgu Tansuğ";
  const [q, setQ] = useState("");
  const [source, setSource] = useState("");
  const [category, setCategory] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null); // null = not searched yet
  const [resultMode, setResultMode] = useState(""); // "direct" when results came from configured RSS feeds
  const [publishingUrl, setPublishingUrl] = useState("");
  const [publishedUrls, setPublishedUrls] = useState(() => new Set());

  const run = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const term = (q || defaultQ).trim();
    if (term.length < 2) { toast.error("En az 2 karakter yazın"); return; }
    setBusy(true);
    try {
      const { data } = await api.get("/admin/press/discover", { params: { q: term, source } });
      setResults(data.results || []);
      setResultMode(data.mode || "");
      if (data.throttled) {
        toast.warning(data.message || "Google Haberler şu an istekleri sınırlıyor. Birkaç dakika sonra tekrar deneyin.");
      } else if (!data.results?.length) {
        toast.info("Bu arama için sonuç bulunamadı (ya da hepsi arşivde var)");
      }
    } catch (e2) {
      toast.error(errMsg(e2) || "Arama başarısız");
      setResults([]);
    }
    setBusy(false);
  };

  const addToArchive = async (r) => {
    setPublishingUrl(r.url);
    try {
      await api.post("/admin/press/discover/publish", {
        url: r.url,
        title: r.title,
        source_name: r.source_name,
        source_domain: r.source_domain,
        image: r.image || "",
        snippet: r.snippet,
        published_at: r.published_at,
        category,
      });
      setPublishedUrls((prev) => new Set([...prev, r.url]));
      qc.invalidateQueries({ queryKey: ["admin", "press"] });
      toast.success(category ? `“${category}” kategorisine eklendi` : "Haber arşive eklendi");
    } catch (e2) {
      const msg = errMsg(e2) || "Eklenemedi";
      if (e2?.response?.status === 409) {
        setPublishedUrls((prev) => new Set([...prev, r.url]));
        toast.info(msg);
      } else if (e2?.response?.status === 429) {
        toast.warning(msg);
      } else {
        toast.error(msg);
      }
    }
    setPublishingUrl("");
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return "";
      return d.toLocaleDateString("tr-TR", { year: "numeric", month: "short", day: "numeric" });
    } catch { return ""; }
  };

  return (
    <section className="rounded-xl border p-4 bg-amber-50/40" data-testid="press-admin-search">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <Search size={14} className="text-amber-700" />
          <h3 className="font-semibold text-sm">Basında ara</h3>
        </div>
        <a href="/admin/ayarlar" className="text-[11px] text-stone-500 hover:text-stone-800 underline" data-testid="press-manage-sources">
          Kaynakları & kategorileri düzenle
        </a>
      </div>
      <p className="text-xs text-stone-500 mb-3">
        Kelimeyi girip <strong>Ara</strong>'ya tıklayın. Haberler orijinal kaynak sitelerinden (başlık, görsel, özet) canlı çekilir; dilediğiniz haberin yanındaki <strong>Arşive Ekle</strong> butonuyla tek tıkla basın arşivinize ekleyebilirsiniz. Daha önce arşive aldığınız haberler listede görünmez (sildiğinizde geri gelir).
      </p>
      <div className="grid sm:grid-cols-[1fr_auto_auto_auto] gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); run(e); } }} placeholder={defaultQ} data-testid="press-admin-search-input" />
        <select value={source} onChange={(e) => setSource(e.target.value)} className="h-9 px-3 rounded-md border text-sm bg-white" data-testid="press-admin-search-source">
          <option value="">Tüm kaynaklar</option>
          {sources.map((s) => <option key={s.domain} value={s.domain}>{s.name}</option>)}
        </select>
        <CategoryPicker
          value={category}
          options={categories}
          onChange={setCategory}
          testid="press-admin-search-category"
          allowBlank
          blankLabel="Kategori seçmeden ekle"
        />
        <Button type="button" onClick={run} disabled={busy} data-testid="press-admin-search-submit">
          {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Search size={14} className="mr-1" />}
          Ara
        </Button>
      </div>

      {results !== null && (
        <div className="mt-4 space-y-2" data-testid="press-admin-search-results">
          {(() => {
            // After "Arşive Ekle" the row should disappear from the list (user
            // request: "ekleme yapıldıktan sonra listeden kalksın"). We keep
            // `publishedUrls` so the backend's duplicate guard still kicks in
            // even if the admin re-runs the search without a page reload.
            const visible = results.filter((r) => !publishedUrls.has(r.url));
            if (visible.length === 0) {
              return (
                <p className="text-xs text-stone-400 text-center py-6">
                  {results.length === 0
                    ? "Sonuç bulunamadı veya tümü zaten arşivinizde. Farklı bir kelime deneyin."
                    : "Tüm sonuçlar arşive eklendi. Yeni bir arama yapabilirsiniz."}
                </p>
              );
            }
            return (
              <>
                <p className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold mb-1 flex items-center gap-2 flex-wrap">
                  <span>{visible.length} sonuç bulundu</span>
                  {resultMode === "direct" ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 normal-case tracking-normal text-[10px]" data-testid="press-admin-mode-direct">
                      Kaynaklarınızdan
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-stone-100 text-stone-600 border normal-case tracking-normal text-[10px]" data-testid="press-admin-mode-fallback">
                      Google News yedeği
                    </span>
                  )}
                  {category && <span className="text-amber-700 normal-case tracking-normal">· eklenince “{category}” altına kaydedilir</span>}
                </p>
                {visible.map((r, i) => {
                  const isBusy = publishingUrl === r.url;
                  return (
                    <div key={r.url || i} className="bg-white border rounded-lg p-3 flex items-start gap-3 hover:border-stone-400 transition-colors" data-testid={`press-admin-result-${i}`}>
                      {r.image ? (
                        <img
                          src={r.image}
                          alt=""
                          loading="lazy"
                          decoding="async"
                          referrerPolicy="no-referrer"
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                          className="shrink-0 w-20 h-14 rounded-md object-cover border bg-stone-100"
                          data-testid={`press-admin-result-${i}-image`}
                        />
                      ) : r.source_domain ? (
                        <div className="shrink-0 w-20 h-14 rounded-md bg-stone-50 border flex items-center justify-center" data-testid={`press-admin-result-${i}-favicon`}>
                          <img
                            src={`https://www.google.com/s2/favicons?sz=64&domain=${encodeURIComponent(r.source_domain)}`}
                            alt=""
                            loading="lazy"
                            decoding="async"
                            referrerPolicy="no-referrer"
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                            className="w-7 h-7 object-contain"
                          />
                        </div>
                      ) : (
                        <div className="shrink-0 w-20 h-14 rounded-md bg-amber-50 border border-amber-200 flex items-center justify-center">
                          <Globe size={18} className="text-amber-700" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <a href={r.url} target="_blank" rel="noreferrer" className="font-medium text-sm text-stone-800 hover:text-amber-700 line-clamp-2 inline-flex items-start gap-1" data-testid={`press-admin-result-${i}-title`}>
                          {r.title}
                          <ExternalLink size={11} className="opacity-60 shrink-0 mt-0.5" />
                        </a>
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1 text-[11px] text-stone-500">
                          {r.source_name && <span className="font-semibold text-amber-800">{r.source_name}</span>}
                          {r.source_domain && <span className="opacity-50">{r.source_domain}</span>}
                          {r.published_at && <>
                            <span className="w-1 h-1 rounded-full bg-stone-400" />
                            <span>{fmtDate(r.published_at)}</span>
                          </>}
                        </div>
                        {r.snippet && <p className="text-xs text-stone-600 line-clamp-2 mt-1">{r.snippet}</p>}
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        disabled={isBusy}
                        onClick={() => addToArchive(r)}
                        data-testid={`press-admin-result-${i}-add`}
                      >
                        {isBusy ? <><Loader2 size={13} className="mr-1 animate-spin" />Ekleniyor</> :
                          <><Plus size={13} className="mr-1" />Arşive Ekle</>}
                      </Button>
                    </div>
                  );
                })}
              </>
            );
          })()}
        </div>
      )}
    </section>
  );
}

/* ------------------- ADD / EDIT ------------------- */

function MethodPicker({ onPick, onBack, prefilledUrl }) {
  return (
    <div className="bg-stone-50 border rounded-xl p-5 space-y-4" data-testid="press-method-picker">
      <div className="flex items-center justify-between">
        <p className="font-semibold text-sm">Nasıl eklemek istersiniz?</p>
        <Button type="button" size="sm" variant="ghost" onClick={onBack} data-testid="press-method-back"><ArrowLeft size={14} className="mr-1" />Vazgeç</Button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <button type="button" onClick={() => onPick("link")} data-testid="press-method-link"
          className="text-left border rounded-xl p-4 bg-white hover:border-stone-900 hover:shadow-sm transition-all">
          <Link2 size={20} className="text-amber-600 mb-2" />
          <p className="font-medium text-sm">Linkten otomatik çek</p>
          <p className="text-xs text-stone-500 mt-1">Haberin linkini yapıştırın; başlık, görsel ve özet otomatik gelsin. Kaynak altta görünür.</p>
          {prefilledUrl && <p className="text-[10px] text-amber-700 mt-2 truncate">Hazır link: {prefilledUrl}</p>}
        </button>
        <button type="button" onClick={() => onPick("manual")} data-testid="press-method-manual"
          className="text-left border rounded-xl p-4 bg-white hover:border-stone-900 hover:shadow-sm transition-all">
          <PenLine size={20} className="text-amber-600 mb-2" />
          <p className="font-medium text-sm">Elle yazarak ekle</p>
          <p className="text-xs text-stone-500 mt-1">Başlık, kendi metniniz, kaynak adı ve kaynak linkini kendiniz yazın.</p>
        </button>
      </div>
    </div>
  );
}

function TagEditor({ value, onChange, testid }) {
  const [t, setT] = useState("");
  const tags = Array.isArray(value) ? value : [];
  const add = () => {
    const v = t.trim();
    if (!v) return;
    if (tags.includes(v)) { setT(""); return; }
    onChange([...tags, v].slice(0, 20));
    setT("");
  };
  const rm = (x) => onChange(tags.filter((y) => y !== x));
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex flex-wrap gap-1.5">
        {tags.map((x) => (
          <span key={x} className="inline-flex items-center gap-1 bg-stone-100 border border-stone-200 rounded-full px-2.5 py-0.5 text-xs">
            <Tag size={10} />{x}
            <button type="button" onClick={() => rm(x)} className="text-stone-500 hover:text-red-600" data-testid={`${testid}-rm-${x}`}><X size={11} /></button>
          </span>
        ))}
        {tags.length === 0 && <span className="text-xs text-stone-400">Henüz etiket yok.</span>}
      </div>
      <div className="flex gap-2">
        <Input value={t} onChange={(e) => setT(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder="Etiket yazıp Enter'a bas" data-testid={`${testid}-input`} />
        <Button type="button" variant="outline" size="sm" onClick={add} data-testid={`${testid}-add`}><Plus size={13} /></Button>
      </div>
    </div>
  );
}

function PressForm({ item, onBack, categories }) {
  const [f, setF] = useState(item);
  const [busy, setBusy] = useState(false);
  const [fetching, setFetching] = useState(false);
  const { save } = useCrud("press");
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const isLink = f.mode === "link";

  const fetchPreview = async () => {
    const url = (f.source_url || "").trim();
    if (!/^https?:\/\//.test(url)) return toast.error("Önce geçerli bir http(s) linki yapıştırın");
    setFetching(true);
    try {
      const { data } = await api.get("/admin/link-preview", { params: { url } });
      setF((p) => ({
        ...p,
        title: { ...(p.title || {}), tr: data.title || p.title?.tr || "" },
        summary: { ...(p.summary || {}), tr: data.summary || p.summary?.tr || "" },
        cover: data.image || p.cover || "",
        source_name: data.source_name || p.source_name || "",
        source_url: data.source_url || url,
      }));
      toast.success("Önizleme getirildi: başlık, özet, görsel ve kaynak dolduruldu.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bağlantı okunamadı");
    } finally {
      setFetching(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.title?.tr?.trim()) return toast.error("Başlık gerekli");
    setBusy(true);
    const ok = await save(f);
    setBusy(false);
    if (ok) onBack();
  };

  return (
    <form onSubmit={submit} className="bg-stone-50 border rounded-xl p-5 space-y-4" data-testid="press-form">
      <div className="flex items-center justify-between">
        <p className="font-semibold text-sm flex items-center gap-2">
          {isLink ? <Link2 size={15} className="text-amber-600" /> : <PenLine size={15} className="text-amber-600" />}
          {item.id ? "Basın haberini düzenle" : isLink ? "Linkten basın haberi" : "Elle basın haberi"}
        </p>
        <div className="flex gap-2">
          <Button type="button" size="sm" variant="ghost" onClick={onBack} data-testid="press-form-back"><ArrowLeft size={14} className="mr-1" />Vazgeç</Button>
          <Button type="submit" size="sm" disabled={busy} data-testid="press-form-save">{busy ? "…" : "Kaydet"}</Button>
        </div>
      </div>

      {isLink && (
        <Field label="Haber linki" hint="Haberin yayınlandığı sayfanın adresi. Önizlemeyi getirince alanlar otomatik dolar.">
          <div className="flex gap-2">
            <Input value={f.source_url || ""} onChange={(e) => set("source_url")(e.target.value)} placeholder="https://..." data-testid="press-link-url" />
            <Button type="button" variant="outline" onClick={fetchPreview} disabled={fetching} data-testid="press-link-fetch">
              <Download size={14} className="mr-1" />{fetching ? "…" : "Getir"}
            </Button>
          </div>
        </Field>
      )}

      <I18nInput label="Haber başlığı" value={f.title} onChange={set("title")} testid="press-title" maxLength={200} />

      {isLink ? (
        <>
          <I18nInput label="Kısa özet (kartta ve haberde görünür)" value={f.summary} onChange={set("summary")} multiline rows={4} testid="press-summary" maxLength={600} />
          {f.cover && <img src={img(f.cover)} alt="" loading="lazy" decoding="async" className="w-full max-w-xs aspect-video object-cover rounded-lg border" />}
          <ImageUpload label="Görsel (otomatik geldi; değiştirebilirsiniz)" value={f.cover} onChange={set("cover")} testid="press-cover" alt={f.title?.tr} />
        </>
      ) : (
        <>
          <RichEditor label="Haberin yazısı (kendi metniniz)" value={f.body} onChange={set("body")} testid="press-body" minH="min-h-[160px]" />
          <ImageUpload label="Kapak görseli (isteğe bağlı)" value={f.cover} onChange={set("cover")} testid="press-cover" alt={f.title?.tr} />
        </>
      )}

      <div className="grid sm:grid-cols-2 gap-3 border-t pt-4">
        <Field label="Haber kaynağı (isim)" hint="Örn. Kıbrıs Postası, Yeni Düzen"><Input value={f.source_name || ""} onChange={(e) => set("source_name")(e.target.value)} placeholder="Kaynak adı" data-testid="press-source-name" /></Field>
        {!isLink && <Field label="Haber kaynağı linki" hint="Yazının altında gösterilir (http/https)"><Input value={f.source_url || ""} onChange={(e) => set("source_url")(e.target.value)} placeholder="https://" data-testid="press-source-url" /></Field>}
      </div>

      <div className="grid sm:grid-cols-2 gap-3 border-t pt-4">
        <Field label="Kategori" hint="Haber grubunu seçin ya da ‘+ Yeni kategori ekle’ ile anında yenisini oluşturun.">
          <CategoryPicker
            value={f.category || ""}
            options={categories}
            onChange={set("category")}
            testid="press-category"
          />
        </Field>
        <Field label="Etiketler (serbest)" hint="Birden çok etiket ekleyebilirsiniz. Enter ile ekle.">
          <TagEditor value={f.tags} onChange={set("tags")} testid="press-tags" />
        </Field>
      </div>

      <label className="flex items-center gap-3 text-sm text-stone-600"><Switch checked={f.status !== "draft"} onCheckedChange={(v) => set("status")(v ? "published" : "draft")} data-testid="press-status" />Yayında</label>
    </form>
  );
}

/* ------------------- LIST ROW ------------------- */

function Row({ it, onEdit, onRemove, selected, onToggle }) {
  return (
    <div className="flex items-center gap-3">
      <Checkbox checked={!!selected} onCheckedChange={() => onToggle(it.id)} data-testid={`press-select-${it.id}`} />
      {it.cover && <img src={img(it.cover)} alt="" className="w-14 h-10 object-cover rounded border shrink-0" loading="lazy" />}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate">{tr(it.title) || "(başlıksız)"}</p>
        <p className="text-xs text-stone-400 truncate flex items-center gap-1">{it.mode === "link" ? <Link2 size={11} /> : <PenLine size={11} />}{it.source_name || "—"}
          {Array.isArray(it.tags) && it.tags.slice(0, 3).map((t) => (
            <span key={t} className="ml-1 inline-flex items-center gap-0.5 bg-stone-100 border border-stone-200 rounded-full px-1.5 text-[10px] text-stone-600"><Tag size={9} />{t}</span>
          ))}
        </p>
      </div>
      {it.status === "draft" && <span className="text-[10px] bg-stone-200 text-stone-600 rounded px-1.5 py-0.5">Taslak</span>}
      {it.source_url && <a href={it.source_url} target="_blank" rel="noreferrer" className="text-stone-400 hover:text-stone-900 p-1" title="Kaynağı aç"><ExternalLink size={14} /></a>}
      <button type="button" onClick={() => onEdit(it)} className="text-stone-500 hover:text-stone-900 p-1" data-testid={`press-edit-${it.id}`}><Pencil size={14} /></button>
      <button type="button" onClick={() => onRemove(it.id, tr(it.title))} className="text-red-400 hover:text-red-600 p-1" data-testid={`press-delete-${it.id}`}><Trash2 size={14} /></button>
    </div>
  );
}

/* ------------------- MAIN ------------------- */

export default function PressManager() {
  const qc = useQueryClient();
  const { data = [] } = useList("press");
  const { data: site } = useQuery({ queryKey: ["site"], queryFn: () => api.get("/public/site").then((r) => r.data) });
  const settingsCategories = site?.press?.categories || [];
  const { remove, reorder } = useCrud("press");
  const [editing, setEditing] = useState(null);
  const [picking, setPicking] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [filter, setFilter] = useState(""); // category filter in list
  const [bulkBusy, setBulkBusy] = useState(false);
  const [backfillBusy, setBackfillBusy] = useState(false);
  const blank = (mode) => ({ mode, title: { tr: "" }, summary: { tr: "" }, body: { tr: "" }, source_name: "", source_url: "", category: "", tags: [], status: "published" });

  const published = data.filter((d) => d.status !== "draft").length;
  const drafts = data.length - published;

  // Grouping by category (settings categories + any custom categories found on items)
  const allCategories = useMemo(() => {
    const set = new Set(settingsCategories);
    data.forEach((d) => { if (d.category) set.add(d.category); });
    return Array.from(set);
  }, [data, settingsCategories]);

  const filtered = useMemo(() => {
    if (!filter) return data;
    if (filter === "__none__") return data.filter((d) => !d.category);
    return data.filter((d) => d.category === filter);
  }, [data, filter]);

  const groups = useMemo(() => {
    const map = new Map();
    filtered.forEach((d) => {
      const k = d.category || UNCATEGORIZED;
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(d);
    });
    // keep settings category order first
    const ordered = [];
    settingsCategories.forEach((c) => { if (map.has(c)) ordered.push([c, map.get(c)]); });
    Array.from(map.entries()).forEach(([k, v]) => {
      if (!ordered.find(([x]) => x === k)) ordered.push([k, v]);
    });
    return ordered;
  }, [filtered, settingsCategories]);

  const toggle = (id) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((d) => d.id)));
  };
  const bulkDelete = async () => {
    if (!selected.size) return;
    if (!window.confirm(`${selected.size} haber silinecek. Emin misiniz?`)) return;
    setBulkBusy(true);
    try {
      const { data: res } = await api.post("/admin/press/bulk-delete", { ids: Array.from(selected) });
      toast.success(`${res.deleted || 0} haber silindi`);
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ["admin", "press"] });
    } catch (e) {
      toast.error(errMsg(e) || "Silme başarısız");
    }
    setBulkBusy(false);
  };

  const backfillCovers = async () => {
    if (backfillBusy) return;
    if (!window.confirm("Eksik kapak fotoğrafları olan ve çözümlenmemiş Google Haberler linkli tüm haberler için asıl kaynak ve kapak çekilecek. Devam edilsin mi?")) return;
    setBackfillBusy(true);
    try {
      const { data: res } = await api.post("/admin/press/backfill-covers");
      const upd = res.updated || 0;
      const skipped = res.skipped_count || 0;
      if (upd && !skipped) toast.success(`${upd} haber güncellendi`);
      else if (upd && skipped) toast.success(`${upd} güncellendi · ${skipped} atlandı`);
      else if (!upd && skipped) toast.warning(`${skipped} haber çözümlenemedi. Lütfen tekrar deneyin.`);
      else toast.info("Düzeltilecek haber bulunamadı");
      qc.invalidateQueries({ queryKey: ["admin", "press"] });
    } catch (e) {
      toast.error(errMsg(e) || "Düzeltme başarısız");
    }
    setBackfillBusy(false);
  };

  return (
    <div className="mt-6 space-y-5" data-testid="press-manager">
      <div className="bg-white border rounded-xl p-5 space-y-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="font-semibold flex items-center gap-2"><Newspaper size={17} className="text-amber-600" />Basın haberleri</p>
            <p className="text-sm text-stone-500">Basın arşivi sayfasında kart ızgarası olarak gösterilir. Sürükleyerek sıralayabilirsiniz.</p>
            <p className="text-[11px] uppercase tracking-widest text-stone-500 mt-2">
              {data.length} toplam · {published} yayında · {drafts > 0 ? `${drafts} taslak` : "taslak yok"}
            </p>
          </div>
          {!editing && !picking && (
            <div className="flex items-center gap-2">
              {data.length > 0 && (
                <Button type="button" size="sm" variant="outline" onClick={backfillCovers} disabled={backfillBusy} data-testid="press-backfill-covers">
                  {backfillBusy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Wand2 size={14} className="mr-1" />}
                  Kapakları düzelt
                </Button>
              )}
              <Button type="button" size="sm" onClick={() => setPicking(true)} data-testid="press-add"><Plus size={14} className="mr-1" />Yeni basın haberi</Button>
            </div>
          )}
        </div>

        {picking && <MethodPicker onBack={() => setPicking(false)} onPick={(mode) => { setPicking(false); setEditing(blank(mode)); }} />}
        {editing && <PressForm item={editing} onBack={() => setEditing(null)} categories={allCategories} />}

        {!editing && !picking && (
          data.length === 0
            ? <p className="text-sm text-stone-400 text-center py-8">Henüz basın haberi yok. Yukarıdaki “Basında ara” kutusuyla haberi bulun, ardından <strong>Yeni basın haberi</strong>'ne tıklayıp arşive ekleyin.</p>
            : (
              <>
                {/* Filter + bulk toolbar */}
                <div className="flex flex-wrap items-center gap-2 border-b pb-3">
                  <div className="flex items-center gap-2 text-xs text-stone-500">
                    <Folder size={13} />
                    <span>Kategori:</span>
                  </div>
                  <select value={filter} onChange={(e) => setFilter(e.target.value)} className="h-8 px-2 rounded-md border text-xs bg-white" data-testid="press-filter-category">
                    <option value="">Hepsi</option>
                    <option value="__none__">Kategorisiz</option>
                    {allCategories.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <div className="flex-1" />
                  <label className="flex items-center gap-2 text-xs text-stone-600">
                    <Checkbox
                      checked={filtered.length > 0 && selected.size === filtered.length}
                      onCheckedChange={toggleAll}
                      data-testid="press-select-all"
                    />
                    Tümünü seç
                  </label>
                  {selected.size > 0 && (
                    <Button type="button" size="sm" variant="destructive" onClick={bulkDelete} disabled={bulkBusy} data-testid="press-bulk-delete">
                      {bulkBusy ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Trash2 size={13} className="mr-1" />}
                      Seçilenleri Sil ({selected.size})
                    </Button>
                  )}
                </div>

                {/* Groups */}
                <div className="space-y-4">
                  {groups.map(([cat, items]) => (
                    <div key={cat} data-testid={`press-group-${cat}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Folder size={13} className="text-amber-600" />
                        <p className="text-xs uppercase tracking-widest font-semibold text-stone-600">{cat}</p>
                        <span className="text-[10px] text-stone-400">({items.length})</span>
                      </div>
                      <SortableList
                        items={items}
                        onReorder={reorder}
                        render={(it) => <Row it={it} onEdit={setEditing} onRemove={remove} selected={selected.has(it.id)} onToggle={toggle} />}
                      />
                    </div>
                  ))}
                </div>
              </>
            )
        )}
      </div>
    </div>
  );
}
